import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Home, Building2, Briefcase, Sofa, Package, PackageOpen, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig, trackPhoneClick } from '../config/business';

const iconMap = { Home, Building2, Briefcase, Sofa, Package, PackageOpen };

const serviceDetails = {
  'house-removals': {
    title: 'House Removals',
    description: 'Complete home moving services for houses of all sizes across Melbourne. From small cottages to large family homes, we handle your belongings with care.',
    features: [
      'Full house packing and unpacking available',
      'Furniture disassembly and reassembly',
      'Careful handling of fragile items',
      'Protection for floors and doorways',
      'Appropriate truck capacity for your home size'
    ]
  },
  'apartment-removals': {
    title: 'Apartment Removals',
    description: 'Specialist apartment and unit moves, including high-rise buildings. We understand the unique challenges of apartment moving, from lift bookings to building access.',
    features: [
      'Experience with high-rise and walk-up apartments',
      'Building access and lift coordination',
      'Efficient handling in tight spaces',
      'Protection for common areas',
      'Studio to multi-bedroom units'
    ]
  },
  'office-removals': {
    title: 'Office Removals',
    description: 'Professional business and office relocations with minimal disruption to your operations. We work efficiently to get your business running in your new location.',
    features: [
      'Desks, chairs and office furniture',
      'IT equipment handling',
      'Filing cabinets and archives',
      'Weekend and after-hours moves available',
      'Minimal business disruption'
    ]
  },
  'furniture-removals': {
    title: 'Furniture Removals',
    description: 'Individual furniture items and deliveries. Perfect for marketplace purchases, single items, or furniture that needs moving between locations.',
    features: [
      'Single item moves',
      'Marketplace pickup and delivery',
      'Furniture assembly assistance',
      'Careful wrapping and protection',
      'Flexible scheduling'
    ]
  },
  'packing': {
    title: 'Packing Services',
    description: 'Professional packing assistance to help prepare your belongings for moving day. Our team can pack efficiently and securely.',
    features: [
      'Professional packing materials',
      'Systematic room-by-room packing',
      'Fragile item specialist packing',
      'Labelling and organisation',
      'Availability depends on move requirements'
    ],
    note: 'Packing service availability and staffing depend on the requirements of your move. Discuss your needs when requesting a quote.'
  },
  'unpacking': {
    title: 'Unpacking Services',
    description: 'Help settling into your new space. Our team can assist with unpacking and getting you set up in your new home or office.',
    features: [
      'Box unpacking and breakdown',
      'Furniture placement assistance',
      'Kitchen and bedroom setup',
      'Rubbish and box removal',
      'Availability depends on move requirements'
    ],
    note: 'Unpacking service availability depends on the requirements of your move. Discuss your needs when requesting a quote.'
  }
};

export default function ServicesPage() {
  const { services, contact, insurance } = businessConfig;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Moving Services Melbourne | House, Apartment & Office Removals | Thrifty Move</title>
        <meta name="description" content="Professional Melbourne moving services from $130/hr: house removals, apartment moves, office relocations, furniture delivery, packing and unpacking. 2 removalists included. Fully insured." />
        <link rel="canonical" href="https://thrifty-move.com.au/services" />
        <meta property="og:title" content="Moving Services Melbourne | From $130/hr | Thrifty Move" />
        <meta property="og:description" content="Professional Melbourne moving services from $130/hr: house, apartment, office, furniture moves. 2 removalists included. Fully insured." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/services" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Moving Services",
            "provider": {
              "@type": "MovingCompany",
              "name": "Thrifty Move",
              "telephone": "1800 233 023"
            },
            "areaServed": {
              "@type": "City",
              "name": "Melbourne"
            },
            "offers": {
              "@type": "Offer",
              "price": "130",
              "priceCurrency": "AUD",
              "unitText": "per hour",
              "description": "From $130/hr including 2 removalists and GST"
            }
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="bg-gray-900 pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Moving Services
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Professional moving solutions for homes and businesses across Melbourne
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-8 py-4">
                  <ArrowRight className="mr-2 w-5 h-5" />
                  Get a Free Quote
                </Button>
              </Link>
              <a href={contact.phoneLink} onClick={() => trackPhoneClick('Services - Call')}>
                <Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-4">
                  <Phone className="mr-2 w-5 h-5" />
                  {contact.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-12">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || Package;
              const details = serviceDetails[service.id] || {};
              
              return (
                <Card 
                  key={service.id} 
                  id={service.id}
                  className="border-2 border-gray-100 hover:border-yellow-400 transition-colors overflow-hidden"
                >
                  <CardContent className="p-0">
                    <div className="grid md:grid-cols-3 gap-0">
                      {/* Left: Icon and Title */}
                      <div className="bg-gray-50 p-6 md:p-8 flex flex-col justify-center">
                        <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mb-4">
                          <Icon className="w-8 h-8 text-gray-900" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">{details.title || service.title}</h2>
                        <p className="text-gray-600">{service.shortDescription}</p>
                      </div>
                      
                      {/* Right: Details */}
                      <div className="md:col-span-2 p-6 md:p-8">
                        <p className="text-gray-700 mb-6">{details.description}</p>
                        
                        {details.features && (
                          <div className="mb-6">
                            <h3 className="font-semibold text-gray-900 mb-3">What's included:</h3>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {details.features.map((feature, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                  <CheckCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {details.note && (
                          <p className="text-sm text-gray-500 italic mb-4 bg-yellow-50 p-3 rounded-lg">
                            {details.note}
                          </p>
                        )}
                        
                        <Link to="/quote">
                          <Button className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold">
                            Get a Quote for {service.title}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insurance Banner */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Fully Insured Moving Services</h2>
            <p className="text-gray-300 mb-2">
              {insurance.description}
            </p>
            <p className="text-gray-400 text-sm">
              Your belongings are protected throughout the entire move.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-gray-800 mb-8 max-w-xl mx-auto">
            Use our online quote system to list your items and get an accurate estimate for your move.
          </p>
          <Link to="/quote">
            <Button className="bg-gray-900 hover:bg-gray-800 text-white font-bold px-10 py-5 text-lg">
              <ArrowRight className="mr-2 w-5 h-5" />
              Get Your Free Quote
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}
